import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { DEFAULT_COMPANY_INFO, normalizeCompanyInfo, resolveCompanyLogoUrl } from './companyInfo';

const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const formatAddressHtml = (value) => escapeHtml(value).replace(/\n/g, '<br>');

/**
 * Generate invoice PDF from HTML element
 * @param {Object} jobCard - Job card data
 * @param {Object} ownerData - Owner data {name, code}
 * @param {string} invoiceNo - Invoice number (e.g., "00000001")
 * @param {Object} companyInfo - Company information object
 * @returns {Promise<Blob>} - PDF blob for download
 */
export const generateInvoicePDF = async (jobCard, ownerData, invoiceNo, companyInfo = DEFAULT_COMPANY_INFO) => {
    try {
        const normalizedCompanyInfo = normalizeCompanyInfo(companyInfo);
        const logoUrl = resolveCompanyLogoUrl(normalizedCompanyInfo);
        const companyContactLine = [normalizedCompanyInfo.company_phone, normalizedCompanyInfo.company_email].filter(Boolean).join(' | ');

        let printElement = document.getElementById('invoice-print-sheet');

        if (!printElement) {
            printElement = document.createElement('div');
            printElement.id = 'invoice-print-sheet';
            printElement.style.position = 'absolute';
            printElement.style.left = '-9999px';
            printElement.style.width = '210mm';
            document.body.appendChild(printElement);
        }

        const invoiceHTML = `
      <div style="
        font-family: Arial, sans-serif;
        padding: 20px;
        max-width: 210mm;
        margin: 0 auto;
        background-color: #fff;
        color: #000;
      ">
        <div style="
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 20px;
          border-bottom: 2px solid #FF5733;
          padding-bottom: 15px;
        ">
          <div style="display: flex; gap: 12px; align-items: flex-start;">
            <img src="${logoUrl}" alt="Company Logo" crossorigin="anonymous" style="width: 58px; height: 58px; object-fit: contain;" />
            <div>
              <h1 style="
                margin: 0;
                font-size: 24px;
                font-weight: bold;
                color: #2a2e30;
                letter-spacing: 0.5px;
              ">${escapeHtml(normalizedCompanyInfo.company_name)}</h1>
              <p style="
                margin: 2px 0 0 0;
                font-size: 12px;
                color: #666;
                font-style: italic;
              ">Invoice</p>
              ${companyContactLine ? `<p style="margin: 6px 0 0 0; font-size: 11px; color: #666;">${escapeHtml(companyContactLine)}</p>` : ''}
              ${normalizedCompanyInfo.company_address ? `<p style="margin: 4px 0 0 0; font-size: 11px; color: #666; line-height: 1.45;">${formatAddressHtml(normalizedCompanyInfo.company_address)}</p>` : ''}
            </div>
          </div>
          <div style="text-align: right;">
            <p style="margin: 0 0 5px 0; font-size: 14px; font-weight: bold;">
              Invoice No: INV_${new Date().getFullYear()}_${invoiceNo}
            </p>
            <p style="margin: 0; font-size: 12px; color: #666;">
              Date: ${new Date().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        })}
            </p>
          </div>
        </div>

        <div style="margin-bottom: 20px;">
          <h3 style="
            margin: 0 0 10px 0;
            font-size: 12px;
            font-weight: bold;
            color: #2a2e30;
            text-transform: uppercase;
            letter-spacing: 1px;
          ">Bill To:</h3>
          <div style="
            padding: 15px;
            background-color: #f9f9f9;
            border: 1px solid #ddd;
            border-radius: 5px;
          ">
            <p style="margin: 0 0 5px 0; font-size: 14px; font-weight: bold;">
              ${escapeHtml(ownerData.name || 'N/A')}
            </p>
            <p style="margin: 0; font-size: 12px; color: #666;">
              Owner Code: ${escapeHtml(ownerData.code || 'N/A')}
            </p>
          </div>
        </div>

        <table style="
          width: 100%;
          margin-bottom: 20px;
          border-collapse: collapse;
          border: 1px solid #ddd;
        ">
          <thead>
            <tr style="background-color: #f0f0f0; border-bottom: 2px solid #ddd;">
              <th style="
                padding: 10px;
                text-align: left;
                font-size: 12px;
                font-weight: bold;
                color: #2a2e30;
              ">Description</th>
              <th style="
                padding: 10px;
                text-align: right;
                font-size: 12px;
                font-weight: bold;
                color: #2a2e30;
              ">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr style="border-bottom: 1px solid #ddd;">
              <td style="padding: 10px; font-size: 12px;">
                <strong>Job Card:</strong> ${escapeHtml(jobCard.job_card_no || 'N/A')}<br>
                <strong>Vehicle:</strong> ${escapeHtml(jobCard.plate_no || 'N/A')} (${escapeHtml(jobCard.make || '')} ${escapeHtml(jobCard.model || '')})<br>
                <strong>Completed:</strong> ${jobCard.completed_date ? new Date(jobCard.completed_date).toLocaleDateString('en-US') : 'N/A'}
              </td>
              <td style="padding: 10px; text-align: right; font-size: 12px; font-weight: bold;">
                KES ${parseFloat(jobCard.total_cost || 0).toFixed(2)}
              </td>
            </tr>
            ${jobCard.parts_cost ? `
            <tr style="border-bottom: 1px solid #ddd; background-color: #fafafa;">
              <td style="padding: 10px; font-size: 11px; color: #666;">
                Parts Cost
              </td>
              <td style="padding: 10px; text-align: right; font-size: 11px; color: #666;">
                KES ${parseFloat(jobCard.parts_cost || 0).toFixed(2)}
              </td>
            </tr>
            ` : ''}
            ${jobCard.labor_cost ? `
            <tr style="border-bottom: 1px solid #ddd; background-color: #fafafa;">
              <td style="padding: 10px; font-size: 11px; color: #666;">
                Labor Cost
              </td>
              <td style="padding: 10px; text-align: right; font-size: 11px; color: #666;">
                KES ${parseFloat(jobCard.labor_cost || 0).toFixed(2)}
              </td>
            </tr>
            ` : ''}
            ${jobCard.external_mechanic_cost && parseFloat(jobCard.external_mechanic_cost) > 0 ? `
            <tr style="border-bottom: 1px solid #ddd; background-color: #fafafa;">
              <td style="padding: 10px; font-size: 11px; color: #666;">
                External Mechanic Cost
              </td>
              <td style="padding: 10px; text-align: right; font-size: 11px; color: #666;">
                KES ${parseFloat(jobCard.external_mechanic_cost || 0).toFixed(2)}
              </td>
            </tr>
            ` : ''}
          </tbody>
          <tfoot>
            <tr style="background-color: #f0f0f0; border-top: 2px solid #ddd; font-weight: bold;">
              <td style="padding: 10px; font-size: 13px;">Total Amount Due:</td>
              <td style="
                padding: 10px;
                text-align: right;
                font-size: 14px;
                color: #FF5733;
              ">
                KES ${parseFloat(jobCard.total_cost || 0).toFixed(2)}
              </td>
            </tr>
          </tfoot>
        </table>

        <div style="margin-bottom: 20px;">
          <h3 style="
            margin: 0 0 10px 0;
            font-size: 12px;
            font-weight: bold;
            color: #2a2e30;
            text-transform: uppercase;
            letter-spacing: 1px;
            border-bottom: 1px solid #ddd;
            padding-bottom: 5px;
          ">Service Summary:</h3>
          <div style="
            padding: 10px;
            background-color: #fafafa;
            border: 1px solid #ddd;
            border-radius: 3px;
            font-size: 11px;
            line-height: 1.6;
          ">
            ${jobCard.diagnosis ? `<p><strong>Diagnosis:</strong> ${escapeHtml(jobCard.diagnosis)}</p>` : ''}
            ${jobCard.work_performed ? `<p><strong>Work Performed:</strong> ${escapeHtml(jobCard.work_performed)}</p>` : ''}
          </div>
        </div>

        <div style="
          margin-top: 30px;
          padding-top: 15px;
          border-top: 1px solid #ddd;
          font-size: 10px;
          color: #666;
          text-align: center;
        ">
          <p style="margin: 5px 0;">
            This invoice is for services rendered. Payment terms: Net 30 days.
          </p>
          <p style="margin: 5px 0;">
            Invoice Date: ${new Date().toLocaleString('en-GB')}
          </p>
          <p style="margin: 5px 0;">
            Copyright ${new Date().getFullYear()} ${escapeHtml(normalizedCompanyInfo.company_name)} - Fleet Management System
          </p>
        </div>
      </div>
    `;

        printElement.innerHTML = invoiceHTML;

        await new Promise(resolve => setTimeout(resolve, 500));

        const canvas = await html2canvas(printElement, {
            scale: 2,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff',
            width: printElement.scrollWidth,
            height: printElement.scrollHeight,
            windowWidth: printElement.scrollWidth,
            windowHeight: printElement.scrollHeight
        });

        const imgWidth = 210;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        const pdf = new jsPDF('p', 'mm', 'a4');
        const imgData = canvas.toDataURL('image/png', 1.0);

        let heightLeft = imgHeight;
        let position = 0;
        const pageHeight = 297;

        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft > 0) {
            position = heightLeft - imgHeight;
            pdf.addPage();
            pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;
        }

        const filename = `Invoice_INV_${new Date().getFullYear()}_${invoiceNo}.pdf`;
        pdf.save(filename);

        if (printElement && printElement.parentNode) {
            printElement.style.left = '-9999px';
        }
    } catch (error) {
        console.error('Error generating invoice PDF:', error);
        throw error;
    }
};

/**
 * Generate multiple invoices sequentially
 * @param {Array} jobCards - Array of job cards
 * @param {Object} ownerData - Owner data
 * @param {Array} results - Invoice results from backend (with invoice_no)
 * @param {Object} companyInfo - Company information object
 * @returns {Promise<void>}
 */
export const generateMultipleInvoices = async (jobCards, ownerData, results, companyInfo = DEFAULT_COMPANY_INFO) => {
    for (let i = 0; i < jobCards.length; i++) {
        try {
            const result = results[i];
            if (result.success && result.invoice_no) {
                await generateInvoicePDF(jobCards[i], ownerData, result.invoice_no, companyInfo);
                if (i < jobCards.length - 1) {
                    await new Promise(resolve => setTimeout(resolve, 800));
                }
            }
        } catch (error) {
            console.error(`Failed to generate PDF for job card ${jobCards[i].job_card_no}:`, error);
        }
    }
};
